import { encode, CurrencyCode, PaymentOptions } from 'bysquare/pay';

const singleLine = (value, fallback = '') => String(value ?? fallback)
  .replace(/[\r\n\t]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

export function normalizeIban(value) {
  return String(value || '').replace(/\s+/g, '').toUpperCase();
}

export function isValidIban(value) {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;

  const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`;
  let remainder = 0;
  for (const character of rearranged) {
    const numeric = character >= 'A' && character <= 'Z'
      ? String(character.charCodeAt(0) - 55)
      : character;
    for (const digit of numeric) remainder = ((remainder * 10) + Number(digit)) % 97;
  }
  return remainder === 1;
}

const normalizeCurrency = (value) => String(value || 'EUR').trim().toUpperCase();

const normalizePayment = ({ amountEUR, amount, currency, variableSymbol, note, iban, beneficiaryName }) => {
  const paymentAmount = Number(amount ?? amountEUR);
  const normalizedIban = normalizeIban(iban);
  const reference = String(variableSymbol || '').replace(/\D/g, '').slice(0, 10);
  return {
    amount: paymentAmount,
    currency: normalizeCurrency(currency),
    iban: normalizedIban,
    variableSymbol: reference,
    note: singleLine(note, 'Fate Room reading').slice(0, 100),
    beneficiaryName: singleLine(beneficiaryName, 'Fate Room reader').slice(0, 70),
  };
};

export function buildSepaQr(args) {
  const payment = normalizePayment(args);
  if (payment.currency !== 'EUR') {
    throw new Error('SEPA QR payments must use EUR.');
  }
  const remittance = [
    payment.variableSymbol ? `VS ${payment.variableSymbol}` : '',
    payment.note,
  ].filter(Boolean).join(' - ').slice(0, 140);

  // EPC069-12: the variable symbol belongs in unstructured remittance,
  // not in the structured creditor-reference field.
  return [
    'BCD',
    '002',
    '1',
    'SCT',
    '',
    payment.beneficiaryName,
    payment.iban,
    `${payment.currency}${payment.amount.toFixed(2)}`,
    '',
    '',
    remittance,
  ].join('\n');
}

export function isCzechIban(value) {
  return normalizeIban(value).startsWith('CZ');
}

export function buildCzechPaymentQr(args) {
  const payment = normalizePayment(args);
  if (!isCzechIban(payment.iban)) {
    throw new Error('Czech QR payments require a Czech IBAN.');
  }
  if (payment.currency !== 'CZK') {
    throw new Error('Czech QR payments must use CZK.');
  }

  // Czech Banking Association QR Platba (SPAYD / SPD 1.0).
  // Keep values delimiter-safe because an asterisk separates SPD fields.
  const message = payment.note.replace(/[*:]/g, ' ').slice(0, 60);
  const fields = [
    'SPD',
    '1.0',
    `ACC:${payment.iban}`,
    `AM:${payment.amount.toFixed(2)}`,
    'CC:CZK',
  ];
  if (payment.variableSymbol) fields.push(`X-VS:${payment.variableSymbol}`);
  if (message) fields.push(`MSG:${message}`);
  return fields.join('*');
}

export function buildPaymentQrOptions(args) {
  const payment = normalizePayment(args);
  if (!Number.isFinite(payment.amount) || payment.amount <= 0) {
    return { options: [], error: 'The payment amount is invalid.' };
  }
  if (!isValidIban(payment.iban)) {
    return { options: [], error: 'The reader IBAN is invalid. Ask the reader to update payment details.' };
  }

  const options = [];
  if (isCzechIban(payment.iban) && payment.currency === 'CZK') {
    options.push({ type: 'QR platba (CZ)', value: buildCzechPaymentQr(args) });
    return { options, error: '' };
  }

  if (payment.currency !== 'EUR') {
    return { options: [], error: 'Only EUR is supported for non-Czech QR payments.' };
  }

  if (payment.iban.startsWith('SK')) {
    try {
      options.push({
        type: 'PAY by square',
        value: encode({
          payments: [{
            type: PaymentOptions.PaymentOrder,
            amount: payment.amount,
            currencyCode: CurrencyCode.EUR,
            variableSymbol: payment.variableSymbol,
            paymentNote: payment.note,
            beneficiary: { name: payment.beneficiaryName },
            bankAccounts: [{ iban: payment.iban }],
          }],
        }),
      });
    } catch {
      // SEPA remains available for banks that do not support PAY by square.
    }
  }

  options.push({ type: isCzechIban(payment.iban) ? 'SEPA QR (EUR)' : 'SEPA QR', value: buildSepaQr(args) });
  return { options, error: '' };
}

export function buildPaymentQrValue(args) {
  const result = buildPaymentQrOptions(args);
  return result.options[0] || { type: '', value: '', error: result.error };
}
